import { Injectable, OnDestroy } from '@angular/core';
import { InMemoryDbService } from 'angular-in-memory-web-api';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  Request,
  Invoice,
  InvoiceStatus,
  Announcement,
  AnnouncementType,
  AnnouncementPriority,
  ClassifiedAd,
  AdCategory,
  DashboardStats,
  CalendarEvent,
  EventType,
  EventSourceType,
} from '../models';
@Injectable({
  providedIn: 'root',
})
export class MockDataService implements InMemoryDbService, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor() {}

  private refreshData(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createDb() {
    const requests: Request[] = this.getRequests();
    const invoices: Invoice[] = this.getInvoices();
    const announcements: Announcement[] = this.getAnnouncements();
    const classifiedAds: ClassifiedAd[] = this.getClassifiedAds();
    const calendarEvents: CalendarEvent[] = this.getCalendarEvents();
    const dashboardStats: DashboardStats = this.getDashboardStats();

    return {
      requests,
      invoices,
      announcements,
      classifiedAds,
      dashboardStats,
      calendarEvents,
    };
  }

  private getRequests(): Request[] {
    return [
      {
        id: '1',
        requestNumber: '#5464674',
        description: 'Main door repair needed',
        status: 'pending',
        clientId: 'client1',
        providerBusinessId: 'business1',
        providerBusinessName: 'Maintenance Services',
        type: 'maintenance',
        createdOn: new Date('2024-08-16T10:00:00').getTime(),
        agentId: 'agent1',
        agentName: 'John Smith',
        clientName: 'Building Resident',
        providerName: 'Maintenance Services',
        providerLogo: 'assets/images/provider1.png',
        dateCreated: '16 Aug 2024 - 10:00 AM',
        agent: 'John Smith',
        unreadMessages: 2,
      },
      {
        id: '2',
        requestNumber: '#5464675',
        description: 'Elevator maintenance required',
        status: 'in_progress',
        clientId: 'client2',
        providerBusinessId: 'business2',
        providerBusinessName: 'Elevator Services',
        type: 'maintenance',
        createdOn: new Date('2024-08-15T14:30:00').getTime(),
        agentId: 'agent2',
        agentName: 'Mike Johnson',
        clientName: 'Building Manager',
        providerName: 'Elevator Services',
        providerLogo: 'assets/images/provider2.png',
        dateCreated: '15 Aug 2024 - 02:30 PM',
        agent: 'Mike Johnson',
        unreadMessages: 5,
      },
      {
        id: '3',
        requestNumber: '#5464676',
        description: 'Parking allocation request',
        status: 'completed',
        clientId: 'client3',
        providerBusinessId: 'business3',
        providerBusinessName: 'Administrative Services',
        type: 'administrative',
        createdOn: new Date('2024-08-10T16:45:00').getTime(),
        agentId: 'agent3',
        agentName: 'Sarah Wilson',
        clientName: 'Resident',
        providerName: 'Administrative Services',
        providerLogo: 'assets/images/provider1.png',
        dateCreated: '10 Aug 2024 - 04:45 PM',
        agent: 'Sarah Wilson',
        unreadMessages: 0,
      },
    ];
  }

  private getInvoices(): any[] {
    return [
      {
        id: 1,
        requestNumber: '#5464674',
        serviceKey: 'invoices.main-door-repair',
        service: 'invoices.main-door-repair',
        amount: 256,
        currency: 'AED',
        dueDate: new Date('2024-09-15'),
        createdDate: new Date('2024-08-16T10:00:00'),
        status: InvoiceStatus.UNPAID,
        descriptionKey: 'invoices.main-door-repair-description',
        description: 'invoices.main-door-repair-description',
      },
      {
        id: 2,
        requestNumber: '#5464675',
        serviceKey: 'invoices.elevator-maintenance',
        service: 'invoices.elevator-maintenance',
        amount: 150,
        currency: 'AED',
        dueDate: new Date('2024-09-10'),
        createdDate: new Date('2024-08-15T14:30:00'),
        status: InvoiceStatus.UNPAID,
        descriptionKey: 'invoices.elevator-maintenance-description',
        description: 'invoices.elevator-maintenance-description',
      },
      {
        id: 3,
        requestNumber: 'REQ-2024-001',
        serviceKey: 'invoices.plumbing-repair',
        service: 'invoices.plumbing-repair',
        amount: 150,
        currency: 'USD',
        dueDate: new Date('2024-09-20'),
        createdDate: new Date('2024-08-10T09:00:00'),
        status: InvoiceStatus.UNPAID,
        descriptionKey: 'invoices.plumbing-repair-description',
        description: 'invoices.plumbing-repair-description',
      },
    ];
  }

  private getAnnouncements(): any[] {
    return [
      {
        id: 1,
        titleKey: 'announcements.elevator-maintenance',
        title: 'announcements.elevator-maintenance',
        contentKey: 'announcements.elevator-maintenance-content',
        content: 'announcements.elevator-maintenance-content',
        type: AnnouncementType.MAINTENANCE,
        priority: AnnouncementPriority.HIGH,
        createdDate: new Date('2024-08-20T10:00:00'),
        updatedDate: new Date('2024-08-20T10:00:00'),
        authorKey: 'announcements.building-management',
        author: 'announcements.building-management',
        isActive: true,
        tags: ['maintenance', 'elevator', 'notice'],
      },
      {
        id: 2,
        titleKey: 'announcements.residents-meeting',
        title: 'announcements.residents-meeting',
        contentKey: 'announcements.residents-meeting-content',
        content: 'announcements.residents-meeting-content',
        type: AnnouncementType.GENERAL,
        priority: AnnouncementPriority.MEDIUM,
        createdDate: new Date('2024-08-18T14:30:00'),
        updatedDate: new Date('2024-08-18T14:30:00'),
        authorKey: 'announcements.residents-committee',
        author: 'announcements.residents-committee',
        isActive: true,
        tags: ['meeting', 'residents', 'important'],
      },
      {
        id: 3,
        titleKey: 'announcements.scheduled-water-maintenance',
        title: 'announcements.scheduled-water-maintenance',
        contentKey: 'announcements.scheduled-water-maintenance-content',
        content: 'announcements.scheduled-water-maintenance-content',
        type: AnnouncementType.MAINTENANCE,
        priority: AnnouncementPriority.MEDIUM,
        createdDate: new Date('2024-01-20T10:00:00'),
        updatedDate: new Date('2024-01-20T10:00:00'),
        authorKey: 'announcements.property-management',
        author: 'announcements.property-management',
        isActive: true,
        tags: ['maintenance', 'water', 'scheduled'],
      },
      {
        id: 4,
        titleKey: 'announcements.emergency-power-outage',
        title: 'announcements.emergency-power-outage',
        contentKey: 'announcements.emergency-power-outage-content',
        content: 'announcements.emergency-power-outage-content',
        type: AnnouncementType.EMERGENCY,
        priority: AnnouncementPriority.CRITICAL,
        createdDate: new Date('2024-01-18T19:45:00'),
        updatedDate: new Date('2024-01-18T19:45:00'),
        authorKey: 'announcements.emergency-response-team',
        author: 'announcements.emergency-response-team',
        isActive: true,
        tags: ['emergency', 'power', 'outage', 'safety'],
      },
    ];
  }

  private getClassifiedAds(): any[] {
    return [
      {
        id: 1,
        titleKey: 'classified-ads.home-furniture-title',
        title: 'classified-ads.home-furniture-title',
        descriptionKey: 'classified-ads.home-furniture-description',
        description: 'classified-ads.home-furniture-description',
        category: AdCategory.FURNITURE,
        price: 500,
        currency: 'AED',
        locationKey: 'classified-ads.dubai-location',
        location: 'classified-ads.dubai-location',
        createdDate: new Date('2024-08-19T16:00:00'),
        updatedDate: new Date('2024-08-19T16:00:00'),
        authorKey: 'classified-ads.ahmed-mohamed',
        author: 'classified-ads.ahmed-mohamed',
        authorId: 1,
        isActive: true,
        contactInfo: '+971501234567',
      },
      {
        id: 2,
        titleKey: 'classified-ads.vintage-dining-table-title',
        title: 'classified-ads.vintage-dining-table-title',
        descriptionKey: 'classified-ads.vintage-dining-table-description',
        description: 'classified-ads.vintage-dining-table-description',
        category: AdCategory.FURNITURE,
        price: 450,
        currency: 'USD',
        locationKey: 'classified-ads.dubai-location',
        location: 'classified-ads.dubai-location',
        createdDate: new Date('2024-01-15T10:30:00'),
        updatedDate: new Date('2024-01-15T10:30:00'),
        authorKey: 'classified-ads.john-smith',
        author: 'classified-ads.john-smith',
        authorId: 2,
        isActive: true,
        contactInfo: '+1-555-0101',
      },
      {
        id: 3,
        titleKey: 'classified-ads.used-car-title',
        title: 'classified-ads.used-car-title',
        descriptionKey: 'classified-ads.used-car-description',
        description: 'classified-ads.used-car-description',
        category: AdCategory.VEHICLES,
        price: 45000,
        currency: 'AED',
        locationKey: 'classified-ads.abu-dhabi-location',
        location: 'classified-ads.abu-dhabi-location',
        createdDate: new Date('2024-08-17T11:30:00'),
        updatedDate: new Date('2024-08-17T11:30:00'),
        authorKey: 'classified-ads.sara-ahmed',
        author: 'classified-ads.sara-ahmed',
        authorId: 2,
        isActive: true,
        contactInfo: '+971509876543',
      },
    ];
  }

  private getCalendarEvents(): any[] {
    return [
      {
        id: 1,
        titleKey: 'announcements.elevator-maintenance',
        title: 'announcements.elevator-maintenance',
        descriptionKey: 'announcements.elevator-maintenance-content',
        description: 'announcements.elevator-maintenance-content',
        date: new Date('2024-08-25T08:00:00'),
        startTime: new Date('2024-08-25T08:00:00'),
        endTime: new Date('2024-08-25T12:00:00'),
        type: EventType.MAINTENANCE,
        sourceType: EventSourceType.MAINTENANCE,
        sourceId: 1,
        color: '#ff6b6b',
        locationKey: 'classified-ads.dubai-location',
        location: 'classified-ads.dubai-location',
      },
      {
        id: 2,
        titleKey: 'announcements.residents-meeting',
        title: 'announcements.residents-meeting',
        descriptionKey: 'announcements.residents-meeting-content',
        description: 'announcements.residents-meeting-content',
        date: new Date('2024-08-28T18:00:00'),
        startTime: new Date('2024-08-28T18:00:00'),
        endTime: new Date('2024-08-28T20:00:00'),
        type: EventType.MEETING,
        sourceType: EventSourceType.ANNOUNCEMENT,
        sourceId: 2,
        color: '#4ecdc4',
        locationKey: 'classified-ads.dubai-location',
        location: 'classified-ads.dubai-location',
      },
    ];
  }

  private getDashboardStats(): DashboardStats {
    return {
      admins: 5,
      properties: 12,
      residents: 48,
      vendors: 8,
      amenityBookings: 15,
      petRegistries: 3,
    };
  }
}
